import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Pause, Volume2, Loader2, SkipForward, BookOpen, Sparkles } from 'lucide-react';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', EVS: '🌿', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' };

export default function AudioVideoPlayer({ params, onNavigate }) {
  const { api, user } = useAuth();
  const [videoData, setVideoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showScript, setShowScript] = useState(false);
  const audioRef = useRef(null);

  const subject = params?.subject || 'Math';
  const chapterNum = params?.chapter || 1;
  const className = user?.class_name || 'Class 8';

  const generateAudio = async () => {
    setLoading(true);
    try {
      const res = await api.post('/ai/generate-audio', {
        subject,
        chapter_number: parseInt(chapterNum),
        class_name: className,
      });
      setVideoData(res.data);

      // Auto-play if audio exists
      if (res.data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio_base64}`);
        audioRef.current = audio;
        audio.ontimeupdate = () => {
          if (audio.duration) setProgress((audio.currentTime / audio.duration) * 100);
        };
        audio.onended = () => { setPlaying(false); setProgress(100); };
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, []);

  // Stop audio and navigate
  const handleNavigate = (page, params) => {
    if (audioRef.current) { 
      audioRef.current.pause(); 
      audioRef.current = null; 
    }
    setPlaying(false);
    onNavigate(page, params);
  };

  return (
    <div className="pb-nav px-4 pt-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="back-from-audio" onClick={() => handleNavigate('chapters', { subject })} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white font-['Outfit']" data-testid="audio-page-title">
            3-Min Explainer
          </h1>
          <p className="text-xs text-slate-500">{subjectIcons[subject]} {subject} Ch{chapterNum} - {className}</p>
        </div>
      </div>

      {!videoData ? (
        /* Generate Button */
        <div className="text-center py-12">
          <div className="w-24 h-24 rounded-full bg-violet-600/10 border border-violet-500/20 flex items-center justify-center mx-auto mb-6">
            <Volume2 className="w-10 h-10 text-violet-400" />
          </div>
          <h2 className="text-lg font-bold text-white font-['Outfit'] mb-2">
            Generate 3-Min Audio Explainer
          </h2>
          <p className="text-sm text-slate-400 mb-6 max-w-sm mx-auto">
            AI will create a child-friendly script and narrate it with a natural voice
          </p>
          <button
            data-testid="generate-audio-btn"
            onClick={generateAudio}
            disabled={loading}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl px-8 h-12 font-semibold text-sm transition-all glow-violet flex items-center justify-center gap-2 mx-auto disabled:opacity-50"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Generating Audio...</>
            ) : (
              <><Sparkles className="w-5 h-5" /> Generate with Voice</>
            )}
          </button>
          {loading && (
            <p className="text-xs text-slate-500 mt-4 animate-pulse">
              Creating script & narration... this takes ~30 seconds
            </p>
          )}
        </div>
      ) : (
        /* Player */
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="audio-player-section">
          {/* Player Card */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 text-center">
            {/* Animated disc */}
            <div className={`w-32 h-32 rounded-full mx-auto mb-5 border-4 border-violet-500/30 flex items-center justify-center relative ${
              playing ? 'animate-spin' : ''
            }`} style={{ animationDuration: '3s' }}>
              <div className="w-20 h-20 rounded-full bg-violet-600/20 flex items-center justify-center">
                <span className="text-3xl">{subjectIcons[subject]}</span>
              </div>
              {playing && (
                <div className="absolute inset-0 rounded-full border-2 border-violet-400 opacity-50 animate-ping" />
              )}
            </div>

            <h2 className="text-lg font-bold text-white font-['Outfit'] mb-1">{videoData.chapter_name}</h2>
            <p className="text-xs text-slate-500">{videoData.duration_estimate} | Voice: {videoData.voice} | {videoData.plays} plays</p>

            {/* Progress bar */}
            <div className="mt-4 mb-3">
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-6">
              <button
                data-testid="audio-restart-btn"
                onClick={() => { if (audioRef.current) { audioRef.current.currentTime = 0; setProgress(0); } }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <SkipForward className="w-5 h-5 rotate-180" />
              </button>
              <button
                data-testid="audio-play-btn"
                onClick={togglePlay}
                disabled={!videoData.has_audio}
                className="w-16 h-16 rounded-full bg-violet-600 hover:bg-violet-700 flex items-center justify-center transition-all glow-violet disabled:opacity-50"
              >
                {playing ? <Pause className="w-7 h-7 text-white" /> : <Play className="w-7 h-7 text-white ml-1" />}
              </button>
              <button
                data-testid="audio-forward-btn"
                onClick={() => { if (audioRef.current) { audioRef.current.currentTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 10); } }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <SkipForward className="w-5 h-5" />
              </button>
            </div>

            {!videoData.has_audio && (
              <p className="text-xs text-amber-400 mt-3 bg-amber-500/10 rounded-lg py-2 px-3">
                Audio generation is processing. Script is available below.
              </p>
            )}
          </div>

          {/* Script Toggle */}
          <button
            data-testid="toggle-script-btn"
            onClick={() => setShowScript(!showScript)}
            className="w-full bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center justify-between hover:border-slate-700 transition-all"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-white font-medium">View Script</span>
            </div>
            <span className="text-xs text-slate-500">{showScript ? 'Hide' : 'Show'}</span>
          </button>

          {showScript && videoData.script && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5" data-testid="script-content">
              <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{videoData.script}</p>
            </motion.div>
          )}

          {/* Rewards */}
          <div className="text-center text-xs text-violet-400 bg-violet-500/5 border border-violet-500/10 rounded-xl py-3">
            +50 XP | +25 Coins | Video Master Badge earned!
          </div>

          {/* Quick Actions */}
          <div className="flex gap-3">
            <button
              data-testid="take-quiz-after-audio"
              onClick={() => handleNavigate('chapters', { subject })}
              className="flex-1 bg-amber-600/10 border border-amber-500/20 text-amber-300 rounded-xl py-3 text-sm font-medium hover:bg-amber-600/20 transition-all"
            >
              Take Quiz Now
            </button>
            <button
              data-testid="back-to-library"
              onClick={() => handleNavigate('library')}
              className="flex-1 bg-slate-800 text-slate-300 rounded-xl py-3 text-sm font-medium hover:bg-slate-700 transition-all"
            >
              Video Library
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
